import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, ArrowUpRight, Info, ChevronRight } from "lucide-react";

const RANGE_OPTIONS = [
  { value: 7,   label: "7 days"  },
  { value: 30,  label: "30 days" },
  { value: 90,  label: "90 days" },
];

const GRADE_COLORS = {
  "Excellent":          "bg-[#E8F0EB] text-[#2D6A4F]",
  "Very Good":          "bg-[#E4EEF7] text-[#28527A]",
  "Good":               "bg-[#F5F0E4] text-[#7A5928]",
  "Average":            "bg-[#FDF3ED] text-[#8A3F1E]",
  "Needs Improvement":  "bg-[#FCECEC] text-[#B94A48]",
};

function Avatar({ name, picture, size = 48, testid }) {
  const initials = (name || "?")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div
      className="rounded-full bg-linen text-ink font-display grid place-items-center overflow-hidden border-2 border-white shadow-sm"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      data-testid={testid}
    >
      {picture
        ? <img src={picture} alt={name} className="w-full h-full object-cover" />
        : <span>{initials}</span>}
    </div>
  );
}

function PodiumCard({ row, place }) {
  // place: 1 (winner), 2, 3
  const cfg = {
    1: { badge: "bg-[#D4A335] text-white", medal: <Trophy className="w-4 h-4" />,
         ring: "ring-2 ring-[#D4A335] shadow-[0_10px_30px_-10px_rgba(212,163,53,0.55)]",
         translate: "lg:-translate-y-4", size: 80, num: 1 },
    2: { badge: "bg-[#B8BCC1] text-white", medal: <Medal className="w-4 h-4" />,
         ring: "ring-1 ring-[#B8BCC1]/60", translate: "", size: 64, num: 2 },
    3: { badge: "bg-[#C58F5D] text-white", medal: <Award className="w-4 h-4" />,
         ring: "ring-1 ring-[#C58F5D]/60", translate: "", size: 64, num: 3 },
  }[place];

  if (!row) {
    return (
      <div className={`rounded-2xl border border-dashed border-[#E8EAE9] p-6 text-center text-ink-soft text-xs ${cfg.translate}`}>
        —
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: place * 0.05, duration: 0.4 }}
      className={`relative rounded-2xl bg-white p-5 pt-9 text-center ${cfg.ring} ${cfg.translate}`}
      data-testid={`podium-place-${place}`}
    >
      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${cfg.badge}`}>
        {cfg.medal} #{cfg.num}
      </div>
      <div className="flex justify-center">
        <Avatar name={row.name} picture={row.picture} size={cfg.size}
                testid={`podium-avatar-${place}`} />
      </div>
      <div className="mt-3 font-medium text-ink truncate" data-testid={`podium-name-${place}`}>
        {row.name}
      </div>
      <div className="mt-2 font-display text-3xl text-ink leading-none"
           data-testid={`podium-score-${place}`}>
        {row.composite_score}
        <span className="text-sm text-ink-soft"> /100</span>
      </div>
      <div className={`mt-3 inline-block text-[11px] px-2.5 py-1 rounded-full ${GRADE_COLORS[row.grade] || GRADE_COLORS.Good}`}>
        {row.grade}
      </div>
    </motion.div>
  );
}

function ProgressBar({ pct, tone = "pine" }) {
  const bg = tone === "pine" ? "bg-pine" : "bg-[#4A6B5D]";
  const p = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="h-1.5 bg-linen rounded-full overflow-hidden w-full">
      <div className={`h-full ${bg} transition-all duration-500`} style={{ width: `${p}%` }} />
    </div>
  );
}

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function LeaderRow({ row, showRevenue }) {
  return (
    <div className="grid grid-cols-[40px_1.6fr_1fr_1fr_.8fr_.8fr_.9fr_1fr] items-center gap-3 py-3 text-sm"
         data-testid={`leaderboard-row-${row.rank}`}>
      <span className="font-mono text-ink-soft tabular-nums">{row.rank}</span>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={row.name} picture={row.picture} size={36} />
        <span className="truncate font-medium text-ink">{row.name}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-display text-lg leading-none">{row.composite_score}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${GRADE_COLORS[row.grade] || GRADE_COLORS.Good}`}>
          {row.grade}
        </span>
      </div>
      <span className="tabular-nums" data-testid={`row-revenue-${row.rank}`}>
        {showRevenue ? fmtINR(row.revenue) : "—"}
      </span>
      <span className="tabular-nums">{row.patients_seen}</span>
      <span className="tabular-nums">{row.treatments_completed}</span>
      <span className="tabular-nums">{row.rating != null ? row.rating.toFixed(1) : "—"}</span>
      <div className="flex items-center gap-2 min-w-0">
        <ProgressBar pct={row.completion_rate ?? 0} />
        <span className="text-xs text-ink-mid shrink-0 w-9 text-right tabular-nums">
          {row.completion_rate != null ? `${Math.round(row.completion_rate)}%` : "—"}
        </span>
      </div>
    </div>
  );
}

function WeightsPanel({ weights, metricLabels }) {
  return (
    <div className="mt-4 p-4 rounded-xl bg-linen/60 border border-[#E8EAE9]"
         data-testid="weights-panel">
      <div className="overline text-ink-soft mb-3">Composite score weights</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        {Object.entries(weights).map(([k, w]) => (
          <div key={k} className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border border-[#E8EAE9]">
            <span className="text-ink-mid truncate">{metricLabels?.[k] || k}</span>
            <span className="font-mono font-medium text-ink tabular-nums">{Math.round(w * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * DoctorLeaderboard — admin/receptionist widget.
 * Renders the Top-3 podium + a ranked table below.
 *
 * Props:
 *  - compact: boolean, if true limits the list to top-N and shows "View all"
 *  - defaultDays: initial window (default 30)
 */
export default function DoctorLeaderboard({ compact = true, defaultDays = 30, showViewAll = true }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(defaultDays);
  const [showWeights, setShowWeights] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: d } = await api.get(`/leaderboard/doctors?days=${days}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(apiError(e, "Failed to load leaderboard"));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const podium = [rows[1], rows[0], rows[2]]; // 2 - 1 - 3 order
  const belowPodium = rows.slice(3);
  const listRows = compact ? belowPodium.slice(0, 4) : belowPodium;
  const showRevenue = rows.some((r) => r.revenue != null);

  return (
    <div className="card-widget" data-testid="doctor-leaderboard-widget">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-ink" />
          <div>
            <div className="overline text-ink-soft">Overall Leaderboard</div>
            <div className="font-display text-xl leading-tight">By Composite Score</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-[#E8EAE9] p-1 bg-white text-xs"
               data-testid="leaderboard-range-toggle">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.value}
                data-testid={`range-${r.value}`}
                onClick={() => setDays(r.value)}
                className={`px-3 py-1.5 rounded-full transition ${
                  days === r.value ? "bg-pine text-linen" : "text-ink-mid hover:text-ink"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            data-testid="toggle-weights"
            onClick={() => setShowWeights((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition ${
              showWeights ? "bg-pine text-linen border-pine" : "bg-white text-ink-mid border-[#E8EAE9] hover:text-ink"
            }`}
          >
            <Info className="w-3.5 h-3.5" /> Show weights
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="py-16 text-center text-sm text-ink-soft" data-testid="leaderboard-loading">
          Crunching the numbers…
        </div>
      )}

      {!loading && error && (
        <div className="py-8 text-sm text-[#B94A48] bg-[#FCECEC] rounded-xl px-4"
             data-testid="leaderboard-error">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="py-12 text-center text-sm text-ink-soft" data-testid="leaderboard-empty">
          No doctors on the team yet. Invite doctors from Settings to start tracking performance.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          {/* Podium */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 pb-2" data-testid="leaderboard-podium">
            <PodiumCard row={podium[0]} place={2} />
            <PodiumCard row={podium[1]} place={1} />
            <PodiumCard row={podium[2]} place={3} />
          </div>

          {/* Table below podium */}
          {listRows.length > 0 && (
            <div className="mt-4 border-t border-[#E8EAE9]">
              <div className="grid grid-cols-[40px_1.6fr_1fr_1fr_.8fr_.8fr_.9fr_1fr] items-center gap-3 pt-4 pb-2 text-[11px] uppercase tracking-widest text-ink-soft">
                <span>Rank</span>
                <span>Doctor</span>
                <span>Composite</span>
                <span>Revenue</span>
                <span>Patients</span>
                <span>Treatments</span>
                <span>Rating</span>
                <span>Completion</span>
              </div>
              <div className="divide-y divide-[#E8EAE9]">
                {listRows.map((r) => (
                  <LeaderRow key={r.doctor_id} row={r} showRevenue={showRevenue} />
                ))}
              </div>
              {compact && belowPodium.length > listRows.length && showViewAll && (
                <div className="pt-4 text-center">
                  <Link
                    to="/app/analytics/doctors"
                    data-testid="link-view-all-doctors"
                    className="inline-flex items-center gap-1.5 text-sm text-ink-mid hover:text-ink px-4 py-2 rounded-full border border-[#E8EAE9] hover:bg-linen"
                  >
                    View all doctors <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {compact && belowPodium.length === 0 && showViewAll && (
            <div className="pt-6 text-center">
              <Link
                to="/app/analytics/doctors"
                data-testid="link-view-all-doctors"
                className="inline-flex items-center gap-1.5 text-sm text-ink-mid hover:text-ink"
              >
                Open full analytics <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}

          {showWeights && (
            <WeightsPanel weights={data.weights} metricLabels={data.metric_labels} />
          )}
        </>
      )}
    </div>
  );
}
